import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IItem } from '../../timeline/store/timeline.types'
import { IOptions } from '../../../common/common.types'
import { IEKGEvent } from '../store/ekgImport.types'
import { IChildbirthOBState } from '../../procedures/childbirthOB/store/childbirthOB.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateChildbirthProcedureProps {
  procedureEvent: IEKGEvent
  childbirthOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface ChildbirthProcedureObject {
  timelineItem: IItem
  childbirthState: IChildbirthOBState
}

const createChildbirthProcedure = (props: CreateChildbirthProcedureProps): ChildbirthProcedureObject => {
  const { procedureEvent, childbirthOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const childbirthOption = childbirthOptions.find(
    (option) => (option.id ?? '').toString() === procedureEvent.procedureAction
  )
  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `By , Action: ${
    childbirthOption?.name || ''
  }, Fetal HR: , Contractions Freq: , Contractions Dur: , Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.CHILDBIRTH_PROCEDURE,
    childbirthOption?.name || '',
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    itemType: 'Procedures',
    event: 'Childbirth / OB',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_CHILDBIRTHOB_PROCEDURE_ITEM,
    defaultHref: TabConstants.CHILDBIRTHOB_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let childbirthState: IChildbirthOBState = {
    key: clientEventId,
    clientCorrelationId,
    action: childbirthOption ?? null,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    fetalHR: '',
    contractionsFreq: '',
    contractionsDur: '',
    variability: null,
    accelerations: null,
    deceleration: null,
    uCStrength: null,
    dTR: null,
    size: '',
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: false,
    updateInputRows: false,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Childbirth',
    rolePerforming: null,
    rolePerformingPNNV: null,
    isEkgImport: 1,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    childbirthState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: childbirthState,
      actionField: (s) => s.action?.name,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    childbirthState = handleNemsisCodeError(error, childbirthState)
  }

  const childbirthProcedureObject: ChildbirthProcedureObject = {
    childbirthState,
    timelineItem,
  }

  return childbirthProcedureObject
}

export default createChildbirthProcedure
